import {FormControl, InputLabel, MenuItem, OutlinedInput, Select} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {i18n} from 'src/i18n';
import authActions from 'src/modules/auth/authActions';
import authSelectors from 'src/modules/auth/authSelectors';
import {
  getSelectableDivisionIds,
  shouldShowCurrentDivisionSwitcher,
} from 'src/modules/auth/currentDivisionScope';
import DivisionService from 'src/modules/division/divisionService';

const MANAGE_DIVISIONS_VALUE = '__manage_divisions__';

const formControlSx = {
  minWidth: 200,
  maxWidth: 'min(560px, 50vw)',
  mr: 1,
  '& .MuiOutlinedInput-root': {color: 'inherit'},
  '& .MuiInputLabel-root': {color: 'inherit'},
  '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(255,255,255,0.35)'},
} as const;

/**
 * División activa (header).
 * - No admin: solo si hay más de una división en alcance.
 * - Rol fijo `admin`: siempre (contexto cargado); select para división activa + entrada al listado `/division`.
 */
function CurrentDivisionHeaderSelect() {
  const dispatch = useDispatch();
  const history = useHistory();
  const tenantAccessContext = useSelector(authSelectors.selectTenantAccessContext);
  const loadStatus = useSelector(authSelectors.selectTenantAccessContextLoadStatus);
  const currentDivisionId = useSelector(authSelectors.selectCurrentDivisionId);
  const roles = useSelector(authSelectors.selectRoles);

  const isTenantAdmin = roles.includes('admin');

  const selectableIds = useMemo(
    () => getSelectableDivisionIds(tenantAccessContext),
    [tenantAccessContext],
  );

  const show =
    loadStatus === 'loaded' &&
    (isTenantAdmin || shouldShowCurrentDivisionSwitcher(tenantAccessContext));

  const [labels, setLabels] = useState<Record<string, string>>({});
  const [namesReady, setNamesReady] = useState(false);

  useEffect(() => {
    if (!show) {
      setNamesReady(false);
      return;
    }
    if (!selectableIds.length) {
      setLabels({});
      setNamesReady(true);
      return;
    }
    let cancelled = false;
    setNamesReady(false);
    const byCtx = tenantAccessContext?.divisionNamesById;

    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        selectableIds.map(async (id) => {
          const fromCtx =
            byCtx && typeof byCtx === 'object' && Object.prototype.hasOwnProperty.call(byCtx, id)
              ? String(byCtx[id] ?? '').trim()
              : '';
          if (fromCtx !== '') {
            if (!cancelled) {
              next[id] = fromCtx;
            }
            return;
          }
          try {
            const row = await DivisionService.find(id);
            if (cancelled) {
              return;
            }
            const raw = row?.nombre != null ? String(row.nombre).trim() : '';
            next[id] = raw !== '' ? raw : i18n('app.currentDivisionUnnamed');
          } catch {
            if (!cancelled) {
              next[id] = i18n('app.currentDivisionUnnamed');
            }
          }
        }),
      );
      if (!cancelled) {
        setLabels((prev) => ({...prev, ...next}));
        setNamesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, selectableIds.join('|'), tenantAccessContext]);

  if (!show) {
    return null;
  }

  const selectValue =
    currentDivisionId && selectableIds.includes(currentDivisionId)
      ? currentDivisionId
      : selectableIds[0] || '';

  const divisionLabel = i18n('app.currentDivision');

  const menuLabel = (id: string) => {
    if (!id) {
      return i18n('app.currentDivisionNone');
    }
    return namesReady ? labels[id] ?? i18n('app.currentDivisionUnnamed') : i18n('app.currentDivisionNamesLoading');
  };

  return (
    <FormControl
      size="small"
      variant="outlined"
      sx={{
        ...formControlSx,
        '& .MuiSelect-select': {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflow: 'visible',
          textOverflow: 'clip',
          lineHeight: 1.35,
          py: 0.75,
        },
      }}>
      <InputLabel id="current-division-header-label" shrink>
        {divisionLabel}
      </InputLabel>
      <Select
        labelId="current-division-header-label"
        label={divisionLabel}
        input={<OutlinedInput label={divisionLabel} notched />}
        value={selectValue}
        displayEmpty
        renderValue={(value) => menuLabel(String(value))}
        onChange={(e) => {
          const v = e.target.value as string;
          if (v === MANAGE_DIVISIONS_VALUE) {
            history.push('/division');
            return;
          }
          dispatch(authActions.doSetCurrentDivision(v || null));
        }}>
        {selectableIds.map((id) => (
          <MenuItem
            key={id}
            value={id}
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              alignItems: 'flex-start',
              py: 1,
            }}>
            {menuLabel(id)}
          </MenuItem>
        ))}
        {isTenantAdmin && (
          <MenuItem
            value={MANAGE_DIVISIONS_VALUE}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              mt: selectableIds.length ? 0.5 : 0,
              whiteSpace: 'normal',
            }}>
            {i18n('app.currentDivisionManage')}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}

export default CurrentDivisionHeaderSelect;
